/**
 * Картинка, обрезанная по рамке сети: CSS-фон с `background-size/position`, без canvas (перенесено из
 * прежнего `PostPreview`, №383). Нужна плиткам контент-плана — пост выглядит так, как уйдёт в сеть.
 * Рамки нет — наибольший кадр пропорции по центру (`rectFromCrop`); пропорция `orig` или размеров
 * не узнать — картинка как есть.
 */
import { useEffect, useState } from "react";

import type { Aspect } from "@/lib/post";
import { rectFromCrop, type Crop } from "@/lib/postPlan";

/** Размеры видимого кадра: из загруженной картинки (браузер учитывает EXIF-поворот), до загрузки — заданные. */
function useNatural(src: string, width: number | null, height: number | null) {
  const [loaded, setLoaded] = useState<{ w: number; h: number } | null>(null);
  useEffect(() => {
    setLoaded(null);
    const img = new Image();
    img.onload = () => setLoaded({ w: img.naturalWidth, h: img.naturalHeight });
    img.src = src;
  }, [src]);
  return loaded ?? (width && height ? { w: width, h: height } : null);
}

export function CroppedImage({
  src,
  width,
  height,
  crop,
  aspect,
  className,
}: {
  src: string;
  width: number | null;
  height: number | null;
  crop: Crop | null;
  aspect: Aspect;
  className?: string;
}) {
  const size = useNatural(src, width, height);
  if (!size) return <div className={`aspect-[4/5] w-full bg-bg-subtle ${className ?? ""}`} />;
  const r = rectFromCrop(crop, size.w, size.h, aspect);
  // фон растянут так, что рамка занимает весь блок; позиция — в долях свободного поля
  const posX = size.w > r.w ? (r.x / (size.w - r.w)) * 100 : 0;
  const posY = size.h > r.h ? (r.y / (size.h - r.h)) * 100 : 0;
  return (
    <div
      role="img"
      aria-label="Кадр поста"
      className={`w-full bg-no-repeat ${className ?? ""}`}
      style={{
        aspectRatio: `${r.w} / ${r.h}`,
        backgroundImage: `url("${src}")`,
        backgroundSize: `${(size.w / r.w) * 100}% ${(size.h / r.h) * 100}%`,
        backgroundPosition: `${posX}% ${posY}%`,
      }}
    />
  );
}
